/**
 * §7 — the parties list. Correcting what the editor created in a hurry.
 *
 * WHY THIS EXISTS
 *
 * The editor creates parties inline, mid-coding, which is right — you meet
 * most of them for the first time while looking at a transaction. But a name
 * typed at speed and a role checked from a default both need somewhere to be
 * fixed, and until this screen there was nowhere: `PATCH /parties/:id` existed
 * and was tested, and nothing in the application called it.
 *
 * DEACTIVATE, NEVER DELETE
 *
 * §6.10's convention for accounts, and for the same reason. A party carrying
 * posted history cannot be removed without orphaning it, so it is deactivated
 * — it stops being offered for new coding and stays attached to everything it
 * is already on. `entryCount` is shown next to the control so the difference
 * between tidying away an unused name and hiding one that is in the books is
 * visible before the click, not after.
 *
 * NO TAX ID FIELD, STILL DELIBERATELY -- FOR A DIFFERENT REASON NOW
 *
 * This comment used to say the column was plain text and that collecting TINs
 * into it as a side effect of a list screen was not a decision to make
 * quietly. That was true and is no longer: migration 0019 encrypts
 * `parties.tax_id` with AES-256-GCM under PII_ENCRYPTION_KEY, and a CHECK
 * refuses anything that is not the envelope.
 *
 * The field is still absent here, because storing it safely is not the same
 * as having a use for it, and a field that collects an SSN so it can sit
 * unread is a liability with no offsetting benefit. It lives on the screen
 * that files the 1099, which exists since 2026-10-09: the Form 1099-NEC
 * worksheet (Form1099NecPage.tsx, issue #49), where a vendor is marked to
 * receive one and its tax id and address are entered. The API still returns
 * `hasTaxId` rather than the number.
 */

import { AccessNotice } from '../components/AccessNotice.js';
import { filesForm990Pf, keepsNetAssets, useBusiness, useCan } from '../auth/AuthProvider.js';
import { RELATIONSHIP_LABELS } from '../components/Form990PfReport.js';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Toast } from '../components/Toast.js';
import { Combobox } from '../components/Combobox.js';
import {
  ApiError,
  api,
  type DisqualifiedRelationship,
  type Party,
  type PartyAlias,
  type CounterpartySummary,
} from '../api.js';

type RoleKey = 'isCustomer' | 'isVendor' | 'isEmployee' | 'isCharity' | 'isDonor';

const BASE_ROLES: Array<[RoleKey, string]> = [
  ['isCustomer', 'Customer'],
  ['isVendor', 'Vendor'],
  ['isEmployee', 'Employee'],
  ['isCharity', 'Charity'],
];

export function PartiesPage() {
  const mayEdit = useCan()('edit_entries');
  // 0084 (§B.17): a non-profit's donors. Offered only where gifts are received.
  const business = useBusiness();
  const ROLES: Array<[RoleKey, string]> = keepsNetAssets(business) ? [...BASE_ROLES, ['isDonor', 'Donor']] : BASE_ROLES;
  // 0085 (§B.18.5): a private foundation's disqualified persons, flagged with how they are one.
  const foundation = filesForm990Pf(business);
  /** A relationship chosen before the flag is set, so the two are sent together. */
  const [relationshipDraft, setRelationshipDraft] = useState<Record<string, DisqualifiedRelationship | ''>>({});
  const [parties, setParties] = useState<Party[]>([]);
  const [search, setSearch] = useState('');
  const [includeInactive, setIncludeInactive] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draftName, setDraftName] = useState('');

  /*
   * §7 — aliases, for the whole tenant in one request.
   *
   * One request per party would be forty requests to draw one table, and this
   * screen already knows every party it is about to render.
   */
  const [aliases, setAliases] = useState<PartyAlias[]>([]);
  /** Whose "add an alias" field is open, and what is in it. */
  const [aliasDraft, setAliasDraft] = useState<{ partyId: string; text: string } | null>(null);

  /*
   * §7 — counterparties with no party yet.
   *
   * What makes aliases usable against a backlog of hundreds. Without it an alias is
   * only ever learned by whoever happens to open the right entry, and
   * `Synergycom` stays unfiled for as long as nobody does.
   */
  const [counterparties, setCounterparties] = useState<CounterpartySummary[]>([]);
  /** Which counterparty's picker is open. */
  const [assigning, setAssigning] = useState<string | null>(null);

  /**
   * §7 — the two halves of this screen.
   *
   * `parties` is the list of entities. `counterparties` is the list of NAMES the
   * bank uses for them. They were one scrolling page and read as one thing; they
   * are two questions and the second is a working list you go through once.
   */
  const [tab, setTab] = useState<'parties' | 'counterparties'>('parties');

  /**
   * MAPPED means the name resolves to a party, by its own name or an alias —
   * NOT that its entries are filed.
   *
   * That distinction is the whole reason this tab exists. The first version
   * conflated them under one heading and showed rows reading
   * `SYNERGY COMPUTER → Synergy Computer` beneath "Counterparties with no
   * party", which is a screen arguing with itself.
   */
  const [mapped, setMapped] = useState<'all' | 'yes' | 'no'>('no');

  /**
   * §7 — creating a party from a counterparty row.
   *
   * Roles are ASKED, not guessed. The editor can default them from how the
   * entry is being coded; here there is no coding to read, and a silent default
   * would put "Uber" down as a customer. Cheap to correct on the other tab, but
   * only if someone notices, and nothing prompts them to.
   */
  const [newParty, setNewParty] = useState<{
    counterparty: string;
    name: string;
    isCustomer: boolean;
    isVendor: boolean;
    isEmployee: boolean;
    isCharity: boolean;
    isDonor: boolean;
  } | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    api
      .listParties(search.trim() === '' ? undefined : search.trim(), includeInactive)
      .then((result) => {
        setParties(result.parties);
        setError(null);
      })
      .catch((caught: unknown) =>
        setError(caught instanceof ApiError ? caught.message : 'Could not load parties.'),
      )
      .finally(() => setLoading(false));
  }, [search, includeInactive]);

  useEffect(load, [load]);

  /*
   * Aliases and the unfiled backlog, reloaded together after any alias change.
   *
   * Together because they are two views of one fact: adding an alias both adds
   * a row to a party and removes a line from the backlog. Refreshing one and
   * not the other would leave the screen contradicting itself.
   *
   * Independent of the party search, deliberately. Searching for a party must
   * not silently narrow the backlog, which is a list of names that have NO
   * party and therefore cannot match a party search at all.
   */
  const loadAliases = useCallback(() => {
    void Promise.all([api.listAllPartyAliases(), api.listCounterparties()])
      .then(([aliasResult, counterpartyResult]) => {
        setAliases(aliasResult.aliases);
        setCounterparties(counterpartyResult.counterparties);
      })
      .catch((caught: unknown) =>
        setError(caught instanceof ApiError ? caught.message : 'Could not load aliases.'),
      );
  }, []);

  useEffect(loadAliases, [loadAliases]);

  async function addAlias(partyId: string, alias: string, partyName: string) {
    try {
      await api.addPartyAlias(partyId, alias);
      setNotice(`“${alias}” now files under ${partyName}`);
      setAliasDraft(null);
      setAssigning(null);
      loadAliases();
    } catch (caught) {
      // The server's sentence names the party that already holds the alias,
      // which is the thing the operator needs in order to fix it.
      setError(caught instanceof ApiError ? caught.message : 'Could not add that alias.');
    }
  }

  /**
   * Create the party, then alias the counterparty to it when they differ.
   *
   * Two calls, and the second is conditional: an alias equal to the party's own
   * name is refused by the server as redundant, because names are matched
   * before aliases. Sending it anyway would surface an error for a sequence
   * that worked.
   */
  async function createPartyFor() {
    if (newParty === null || newParty.name.trim() === '') return;
    const name = newParty.name.trim();
    try {
      const { party: created } = await api.createParty({
        name,
        isCustomer: newParty.isCustomer,
        isVendor: newParty.isVendor,
        isEmployee: newParty.isEmployee,
        isCharity: newParty.isCharity,
        isDonor: newParty.isDonor,
      });
      if (name.toLowerCase() !== newParty.counterparty.trim().toLowerCase()) {
        await api.addPartyAlias(created.id, newParty.counterparty);
      }
      setNotice(`“${newParty.counterparty}” now files under ${name}`);
      setNewParty(null);
      setAssigning(null);
      load();
      loadAliases();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not create that party.');
    }
  }

  async function dropAlias(alias: PartyAlias) {
    try {
      await api.removePartyAlias(alias.id);
      setNotice(`“${alias.alias}” no longer files automatically`);
      loadAliases();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not remove that alias.');
    }
  }

  /**
   * Every mutation goes through here so one failure path is written once. The
   * server's sentence is shown as-is: a duplicate name says which name, and
   * that is more use than "could not save".
   */
  async function patch(party: Party, body: Parameters<typeof api.updateParty>[1], said: string) {
    try {
      const updated = await api.updateParty(party.id, body);
      setParties((current) =>
        current
          .map((candidate) => (candidate.id === party.id ? updated.party : candidate))
          .sort((a, b) => a.name.localeCompare(b.name)),
      );
      setNotice(said);
      setError(null);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not save that change.');
    }
  }

  const shown = useMemo(
    () => [...parties].sort((a, b) => a.name.localeCompare(b.name)),
    [parties],
  );

  /**
   * The counterparty rows this filter shows.
   *
   * `mapped` asks whether the NAME resolves, not whether its entries are filed.
   */
  const visibleCounterparties = useMemo(
    () =>
      counterparties.filter((row) =>
        mapped === 'all' ? true : mapped === 'yes' ? row.partyId !== null : row.partyId === null,
      ),
    [counterparties, mapped],
  );

  return (
    <section className="parties">
      <header className="row triage-head">
        <h1>Parties</h1>
        <span className="muted">
          {loading
            ? 'Loading…'
            : tab === 'parties'
              ? `${shown.length} shown`
              : `${visibleCounterparties.length} of ${counterparties.length}`}
        </span>
      </header>
      <AccessNotice capability="edit_entries" what="parties" />

      {/*
        Two questions, not one scrolling page. Who the entities are, and what
        the bank calls them. Tabs rather than a filter because you are only ever
        doing one of the two, and the counterparty list is a working list you go
        through once.
      */}
      <nav className="row tabs" aria-label="Parties views">
        {(
          [
            ['parties', 'Parties'],
            ['counterparties', 'Counterparties'],
          ] as Array<['parties' | 'counterparties', string]>
        ).map(([key, caption]) => (
          <button
            key={key}
            type="button"
            className={`tab${tab === key ? ' is-current' : ''}`}
            aria-current={tab === key}
            onClick={() => setTab(key)}
          >
            {caption}
          </button>
        ))}
      </nav>

      {tab === 'parties' && (
        <div className="row triage-filters">
          <label>
            Search{' '}
            <input
              type="search"
              value={search}
              placeholder="name"
              onChange={(event) => setSearch(event.target.value)}
            />
          </label>
          <label>
            <input
              type="checkbox"
              checked={includeInactive}
              onChange={(event) => setIncludeInactive(event.target.checked)}
            />{' '}
            Include deactivated
          </label>
        </div>
      )}

      {tab === 'counterparties' && (
        <div className="row triage-filters">
          <label>
            Has party{' '}
            <select
              aria-label="Has party"
              value={mapped}
              onChange={(event) => setMapped(event.target.value as 'all' | 'yes' | 'no')}
            >
              {/*
                "No" leads, because it is the working list: the names that map
                to nothing are the ones worth an alias. The other two are for
                checking what a mapping actually did.
              */}
              <option value="no">No</option>
              <option value="yes">Yes</option>
              <option value="all">All</option>
            </select>
          </label>
        </div>
      )}

      {notice && !error && (
        <Toast message={notice} variant="ok" onDismiss={() => setNotice(null)} />
      )}
      {error && (
        <p role="alert" className="error">
          {error}
        </p>
      )}

      {tab === 'parties' && (
      <table className="lines parties-table">
        <thead>
          <tr>
            <th>Name</th>
            {/*
              "Also known as" rather than "Aliases". The operator's word for
              this is the bank showing a different name, not a database term.
            */}
            <th>Also known as</th>
            <th>Roles</th>
            <th className="num">Entries</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {shown.map((party) => (
            <tr key={party.id} className={party.isActive ? undefined : 'is-inactive'}>
              <td>
                {editingId === party.id ? (
                  <span className="row rename">
                    <input
                      aria-label={`Rename ${party.name}`}
                      value={draftName}
                      onChange={(event) => setDraftName(event.target.value)}
                    />
                    <button
                      type="button"
                      className="link"
                      disabled={draftName.trim() === ''}
                      onClick={async () => {
                        await patch(party, { name: draftName.trim() }, 'Renamed');
                        setEditingId(null);
                      }}
                    >
                      Save
                    </button>
                    <button type="button" className="link" onClick={() => setEditingId(null)}>
                      Cancel
                    </button>
                  </span>
                ) : (
                  <span className="row rename">
                    <span>{party.name}</span>
                    {!party.isActive && <span className="tag">deactivated</span>}
                    {mayEdit && (
                      <button
                        type="button"
                        className="link"
                        aria-label={`Rename ${party.name}`}
                        onClick={() => {
                          setEditingId(party.id);
                          setDraftName(party.name);
                        }}
                      >
                        Rename
                      </button>
                    )}
                  </span>
                )}
              </td>

              {/*
                §7 — the other names this party arrives under.

                Case is NOT one of them: `Synergy Computer` and
                `SYNERGY COMPUTER` already resolve to the same party, because
                matching normalises with lower(btrim(...)). Only genuinely
                different names need a row, which is why an alias equal to the
                party's own name is refused rather than stored.
              */}
              <td>
                <span className="alias-list">
                  {aliases
                    .filter((alias) => alias.partyId === party.id)
                    .map((alias) => (
                      <span key={alias.id} className="tag alias-tag">
                        {alias.alias}
                        {mayEdit && (
                          <button
                            type="button"
                            className="link"
                            aria-label={`Remove alias ${alias.alias}`}
                            onClick={() => void dropAlias(alias)}
                          >
                            ×
                          </button>
                        )}
                      </span>
                    ))}
                  {aliasDraft?.partyId === party.id ? (
                    <span className="row rename">
                      <input
                        aria-label={`New alias for ${party.name}`}
                        value={aliasDraft.text}
                        onChange={(event) =>
                          setAliasDraft({ partyId: party.id, text: event.target.value })
                        }
                      />
                      <button
                        type="button"
                        className="link"
                        disabled={aliasDraft.text.trim() === ''}
                        onClick={() =>
                          void addAlias(party.id, aliasDraft.text.trim(), party.name)
                        }
                      >
                        Save
                      </button>
                      <button
                        type="button"
                        className="link"
                        onClick={() => setAliasDraft(null)}
                      >
                        Cancel
                      </button>
                    </span>
                  ) : mayEdit ? (
                    <button
                      type="button"
                      className="link"
                      aria-label={`Add an alias for ${party.name}`}
                      onClick={() => setAliasDraft({ partyId: party.id, text: '' })}
                    >
                      Add
                    </button>
                  ) : null}
                </span>
              </td>

              <td>
                {/*
                  Edited in place. A party created inline gets its role from a
                  default, and this is where a wrong one is corrected -- the
                  reason the screen exists at all.
                */}
                <span className="party-roles">
                  {/* Without edit_entries the roles are read, as words. */}
                  {!mayEdit &&
                    (ROLES.filter(([key]) => party[key] === true)
                      .map(([, caption]) => caption)
                      .join(', ') || <span className="muted">None</span>)}
                  {mayEdit && ROLES.map(([key, caption]) => (
                    <label key={key}>
                      <input
                        type="checkbox"
                        aria-label={`${caption} — ${party.name}`}
                        checked={party[key] === true}
                        onChange={(event) =>
                          void patch(
                            party,
                            { [key]: event.target.checked },
                            `${party.name}: ${caption.toLowerCase()} ${
                              event.target.checked ? 'added' : 'removed'
                            }`,
                          )
                        }
                      />{' '}
                      {caption}
                    </label>
                  ))}
                  {/* FD-1: a flag set while a foundation is read, and cleared, after the business stops being one. */}
                  {!mayEdit && party.isDisqualifiedPerson && party.disqualifiedRelationship && (
                    <span>Disqualified Person ({RELATIONSHIP_LABELS[party.disqualifiedRelationship]})</span>
                  )}
                  {mayEdit && (foundation || party.isDisqualifiedPerson === true) && (
                    <DisqualifiedControls
                      party={party}
                      draft={relationshipDraft[party.id] ?? ''}
                      onDraft={(value) => setRelationshipDraft((current) => ({ ...current, [party.id]: value }))}
                      onPatch={(body, said) => void patch(party, body, said)}
                    />
                  )}
                </span>
              </td>

              {/* What deactivating would affect, next to the control that does it. */}
              <td className="num">{party.entryCount}</td>

              <td>
                {mayEdit && (
                  <button
                    type="button"
                    className="link"
                    aria-label={`${party.isActive ? 'Deactivate' : 'Reactivate'} ${party.name}`}
                    onClick={() =>
                      void patch(
                        party,
                        { isActive: !party.isActive },
                        party.isActive
                          ? `${party.name} deactivated — it will not be offered for new categorization`
                          : `${party.name} is available again`,
                      )
                    }
                  >
                    {party.isActive ? 'Deactivate' : 'Reactivate'}
                  </button>
                )}
              </td>
            </tr>
          ))}
          {!loading && shown.length === 0 && (
            <tr>
              <td colSpan={5} className="muted">
                {search.trim() === ''
                  ? 'No parties yet. They are added while categorizing an entry.'
                  : `No party matches ${JSON.stringify(search.trim())}.`}
              </td>
            </tr>
          )}
        </tbody>
      </table>
      )}

      {/*
        §7 — the counterparty list.

        Every distinct name the bank has used, and the party it maps to. Posted
        entries are INCLUDED, and since migration 0027 those in an open period
        count as fillable too: a posted entry can take a party until the books
        close over it. An alias also files every future entry carrying that
        name, and a counterparty missing from a screen named after
        counterparties is the worse answer.

        "Has party" filters on whether the NAME MAPS, not on whether its entries
        are filed. The first version conflated the two and showed rows reading
        `SYNERGY COMPUTER → Synergy Computer` beneath a heading that said they
        had no party.
      */}
      {tab === 'counterparties' && (
        <table className="lines" aria-label="Counterparties">
          <thead>
            <tr>
              <th>Counterparty</th>
              <th className="num">Entries</th>
              <th>Party</th>
            </tr>
          </thead>
          <tbody>
            {visibleCounterparties.map((row) => (
              <tr key={row.counterparty}>
                <td>
                  {row.counterparty}
                  {/*
                    Said only when it changes what an action MEANS. An alias on
                    a counterparty with nothing left to fill -- every entry
                    filed, or fixed by a closed period -- still files future
                    ones, and without this the operator would expect the count
                    beside it to move and it never would.
                  */}
                  {row.fillableHeaders === 0 && row.partyId === null && (
                    <span className="muted"> · nothing left to fill</span>
                  )}
                </td>
                <td className="num">{row.headers}</td>
                <td>
                  {row.partyId !== null ? (
                    <span className="muted">
                      {row.partyName}
                      {row.matchedOn === 'alias' ? ' (alias)' : ''}
                    </span>
                  ) : assigning === row.counterparty ? (
                    <span className="row rename">
                      {/*
                        The same picker the editor uses, so an unmatched search
                        offers to CREATE. A plain select could only choose from
                        what exists, and most of these names have no party yet —
                        which is the entire reason the row is here.
                      */}
                      <Combobox
                        label={`Party for ${row.counterparty}`}
                        value=""
                        options={parties
                          .filter((candidate) => candidate.isActive)
                          .map((candidate) => ({ id: candidate.id, label: candidate.name }))}
                        placeholder="Search or add a party…"
                        onChange={(chosen) =>
                          void addAlias(
                            chosen,
                            row.counterparty,
                            parties.find((c) => c.id === chosen)?.name ?? 'it',
                          )
                        }
                        onCreate={(name) =>
                          setNewParty({
                            counterparty: row.counterparty,
                            name,
                            isCustomer: false,
                            isVendor: false,
                            isEmployee: false,
                            isCharity: false,
                            isDonor: false,
                          })
                        }
                        createLabel={(name) => `Add “${name}” as a new party`}
                        suggestedCreateName={row.counterparty}
                      />
                      <button
                        type="button"
                        className="link"
                        onClick={() => {
                          setAssigning(null);
                          setNewParty(null);
                        }}
                      >
                        Cancel
                      </button>
                    </span>
                  ) : !mayEdit ? null : (
                    <button
                      type="button"
                      className="link"
                      /*
                        Named for the row it is on. A column of identical "Add"
                        buttons is unusable with a screen reader and ambiguous
                        to a test, which is how the last one was found.
                      */
                      aria-label={`Add a party for ${row.counterparty}`}
                      onClick={() => setAssigning(row.counterparty)}
                    >
                      Add
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {!loading && visibleCounterparties.length === 0 && (
              <tr>
                <td colSpan={3} className="muted">
                  {mapped === 'no'
                    ? 'Every counterparty maps to a party.'
                    : 'No counterparty matches this filter.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}

      {/*
        §7 — creating a party from a counterparty.

        ROLES ARE ASKED, not guessed. The editor can default them from how the
        entry is being coded; here there is nothing to read them from, and a
        silent default would file "Uber" as a customer. Correcting it on the
        other tab is one click, but only once somebody notices, and nothing
        would prompt them to.
      */}
      {newParty !== null && (
        <div className="new-party" role="group" aria-label="New party">
          <label className="party-field">
            <span>Name</span>
            <input
              aria-label="New party name"
              value={newParty.name}
              onChange={(event) => setNewParty({ ...newParty, name: event.target.value })}
            />
          </label>
          <fieldset className="party-roles">
            <legend>What are they?</legend>
            {ROLES.map(([key, caption]) => (
              <label key={key}>
                <input
                  type="checkbox"
                  aria-label={`${caption} — new party`}
                  checked={newParty[key]}
                  onChange={(event) =>
                    setNewParty({ ...newParty, [key]: event.target.checked })
                  }
                />{' '}
                {caption}
              </label>
            ))}
          </fieldset>
          {/*
            Stated, because the second half is not obvious: creating a party
            called something other than the counterparty also files the
            counterparty as an alias, or the row would not go away.
          */}
          <p className="muted">
            “{newParty.counterparty}” will file under this party.
          </p>
          <div className="row">
            <button
              type="button"
              disabled={newParty.name.trim() === ''}
              onClick={() => void createPartyFor()}
            >
              Create and file it
            </button>
            <button type="button" className="link" onClick={() => setNewParty(null)}>
              Cancel
            </button>
          </div>
        </div>
      )}

    </section>
  );
}

/**
 * A party's disqualified-person flag and how they are one (0085). The flag is
 * sent with the relationship chosen beside it, so a flag with none is refused
 * by the API in a sentence rather than silently ignored here; a relationship
 * chosen while unflagged waits for the flag, and one changed on a flagged
 * party is saved at once.
 */
function DisqualifiedControls({
  party,
  draft,
  onDraft,
  onPatch,
}: {
  party: Party;
  draft: DisqualifiedRelationship | '';
  onDraft: (value: DisqualifiedRelationship | '') => void;
  onPatch: (body: Parameters<typeof api.updateParty>[1], said: string) => void;
}) {
  const flagged = party.isDisqualifiedPerson === true;
  const relationship = flagged ? (party.disqualifiedRelationship ?? '') : draft;
  return (
    <span className="disqualified">
      <label>
        <input
          type="checkbox"
          aria-label={`Disqualified Person — ${party.name}`}
          checked={flagged}
          onChange={(event) => {
            const checked = event.target.checked;
            onPatch(
              checked ? { isDisqualifiedPerson: true, disqualifiedRelationship: draft === '' ? null : draft } : { isDisqualifiedPerson: false },
              `${party.name}: disqualified person ${checked ? 'marked' : 'cleared'}`,
            );
          }}
        />{' '}
        Disqualified Person
      </label>{' '}
      <select
        aria-label={`How ${party.name} Is Disqualified`}
        value={relationship}
        onChange={(event) => {
          const value = event.target.value as DisqualifiedRelationship | '';
          if (!flagged) {
            onDraft(value);
            return;
          }
          onPatch(
            { disqualifiedRelationship: value === '' ? null : value },
            `${party.name}: ${value === '' ? 'relationship cleared' : RELATIONSHIP_LABELS[value].toLowerCase()}`,
          );
        }}
      >
        <option value="">How They Are One</option>
        {(Object.keys(RELATIONSHIP_LABELS) as DisqualifiedRelationship[]).map((key) => (
          <option key={key} value={key}>
            {RELATIONSHIP_LABELS[key]}
          </option>
        ))}
      </select>
    </span>
  );
}
